import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  AuctionFilterCar,
  AuctionSellerRequest,
  AuctionVehiclesFilterRequestBody,
} from '@dms/api/core';
import {CountryAlpha3CodeEnum} from '@dms/api/shared';

import {sourcingFilterConstants} from '../../../constants/sourcingFilterConstants';
import {FiltersType} from '../types/FiltersType';

export function createAuctionFilterBody(
  filters: FiltersType,
  country: CountryAlpha3CodeEnum
): AuctionVehiclesFilterRequestBody {
  const createRangeFilter = (max: number, value?: [number, number]) => {
    if (isNil(value)) {
      return undefined;
    }
    if (value[1] === max) {
      return {gte: value[0]};
    }
    return {gte: value[0], lte: value[1]};
  };

  const createArrayFilter = (value?: string[]) => (isNotNilOrEmpty(value) ? value : undefined);

  const createCarArray = (): AuctionFilterCar[] | undefined => {
    const car: AuctionFilterCar[] = [];

    filters.make?.forEach((makeKey) => {
      const modelKeysOfThisMake =
        filters.model?.filter((modelKey) => {
          const split = modelKey.split('-');
          return split[0] === makeKey;
        }) ?? [];

      if (!modelKeysOfThisMake.length) {
        car.push({make: makeKey});
        return;
      }

      modelKeysOfThisMake.forEach((modelKey) => car.push({model: modelKey}));
    });

    return car.length ? car : undefined;
  };

  const sellerObject: AuctionSellerRequest = {
    country: isNotNilOrEmpty(filters.sellerCountry) ? filters.sellerCountry : undefined,
    types: isNotNilOrEmpty(filters.sellerType) ? filters.sellerType : undefined,
    server: isNotNilOrEmpty(filters.auctionServer) ? filters.auctionServer : undefined,
  };

  const requestBody: AuctionVehiclesFilterRequestBody = {
    buyer: {country},
    seller: Object.values(sellerObject).some(Boolean) ? sellerObject : undefined,
    car: createCarArray(),
    year: filters.year
      ? {gte: filters.year[0], lte: filters.year[1]}
      : {gte: sourcingFilterConstants.YEAR_LIMIT_MIN},
    fuelType: createArrayFilter(filters.fuelType),
    driveType: createArrayFilter(filters.drive),
    transmission: createArrayFilter(filters.transmission),
    bodyType: createArrayFilter(filters.bodyType),
    power: createRangeFilter(sourcingFilterConstants.POWER_LIMIT_MAX, filters.power),
    mileageRange: createRangeFilter(
      sourcingFilterConstants.MILEAGE_RANGE_MAX,
      filters.mileageRange
    ),
    numberOfSeats: createRangeFilter(
      sourcingFilterConstants.NUMBER_OF_SEATS_MAX,
      filters.numberOfSeats
    ),
    featureScore: filters.featureScore
      ? {
          // Division by 100 converts percentage to decimal for API compatibility
          gte: filters.featureScore[0] / 100,
          lte: filters.featureScore[1] / 100,
        }
      : undefined,
    doorCount: createArrayFilter(filters.doorCount),
    colors: filters.selectedColors?.length
      ? filters.selectedColors.map((color) => ({color: color.key}))
      : undefined,
    margin: filters.isMarginAbsolute
      ? createRangeFilter(sourcingFilterConstants.MARGIN_LIMIT_MAX, filters.margin)
      : undefined,
    marginPercent: !filters.isMarginAbsolute
      ? createRangeFilter(sourcingFilterConstants.MARGIN_PERCENT_LIMIT_MAX, filters.marginPercent)
      : undefined,
    cubicCapacity: createRangeFilter(
      sourcingFilterConstants.CUBIC_CAPACITY_LIMITS[1],
      filters.cubicCapacity
    ),
    vatReclaimable: filters.reclaimableVat ? filters.reclaimableVat : undefined,
    turnover: createRangeFilter(sourcingFilterConstants.LIQUIDITY_MAX, filters.liquidity),
    priceActual: createRangeFilter(sourcingFilterConstants.PRICE_MAX, filters.priceCZK),
    pricePercentile: filters.pricePosition
      ? // Division by 100 converts percentage to decimal for API compatibility
        {gte: filters.pricePosition[0] / 100, lte: filters.pricePosition[1] / 100}
      : undefined,
    features: createArrayFilter(filters.features),
    adTitleSearchQuery: filters.adTitle ?? undefined,
    location:
      filters.address?.gps?.lat && filters.address?.gps?.lng && filters.radius
        ? {
            lat: Number.parseFloat(filters.address.gps.lat),
            lon: Number.parseFloat(filters.address.gps.lng),
            distance: filters.radius,
          }
        : undefined,
    adState: {
      includeSuspicious: !filters.hideSuspicious,
      onlyHitlist: filters.hitlistOnly ?? undefined,
      includeBlacklist: !filters.hideBlacklist,
    },
    auctionType: createArrayFilter(filters.auctionType),
    carStatus: createArrayFilter(filters.carStatus),
  };

  return requestBody;
}
